import React, { useRef, useState } from 'react';

import { Calendar, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import Text from '@/components/ui/text';
import {
  type InteractiveSize,
  getIconPixels,
  interactiveSizes,
} from '@/theme/size-tokens';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type DateRangePreset = {
  value: string;
  label: string;
};

export type DateRangeValue = {
  preset: string;
  from: Date | undefined;
  to: Date | undefined;
};

export type DateRangePickerProps = {
  /** Current value — preset key + resolved dates */
  value: DateRangeValue;
  /** Called when the user selects a preset or applies a custom range */
  onChange: (next: DateRangeValue) => void;
  /** Preset options shown in the chevron dropdown */
  presets: DateRangePreset[];
  /** Key that identifies the "custom" preset (default "custom") */
  customPresetKey?: string;
  /** Size variant */
  size?: InteractiveSize;
  /** Additional class names applied to the root element */
  className?: string;
  /** Content rendered as a trailing section inside the picker border box (e.g. live indicator) */
  statusSlot?: React.ReactNode;
  /** Resolve a preset key to a date range (called when a preset is selected) */
  resolvePreset?: (key: string) => { from: Date; to: Date } | null;
};

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatDateShort(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function parseDateInput(value: string): Date | null {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfDay(date: Date): Date {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
}

// ---------------------------------------------------------------------------
// Segmented time input
// ---------------------------------------------------------------------------

type TimeParts = { hours: number; minutes: number; period: 'AM' | 'PM' };

function dateToTimeParts(date: Date): TimeParts {
  const h = date.getHours();
  return {
    hours: h % 12 || 12,
    minutes: date.getMinutes(),
    period: h >= 12 ? 'PM' : 'AM',
  };
}

function timePartsTo24(parts: TimeParts): { h: number; m: number } {
  let h = parts.hours;
  if (parts.period === 'PM' && h !== 12) h += 12;
  if (parts.period === 'AM' && h === 12) h = 0;
  return { h, m: parts.minutes };
}

/**
 * Segmented time input — three individually focusable segments for
 * hours, minutes, and AM/PM with arrow-key cycling and digit overwrite.
 */
function SegmentedTimeInput({
  value,
  onChange,
  onFocus,
  ringClass,
}: Readonly<{
  value: TimeParts;
  onChange: (next: TimeParts) => void;
  onFocus?: () => void;
  ringClass?: string;
}>) {
  const hourRef = useRef<HTMLSpanElement>(null);
  const minRef = useRef<HTMLSpanElement>(null);
  const periodRef = useRef<HTMLSpanElement>(null);

  // Pending digit buffer for two-keystroke entry (e.g. type "1" then "2" → 12)
  const pendingDigitRef = useRef<number | null>(null);
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPending = () => {
    if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
    pendingDigitRef.current = null;
  };

  const clampHour = (n: number) => Math.max(1, Math.min(12, n));
  const clampMinute = (n: number) => ((n % 60) + 60) % 60;

  const handleHourKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      clearPending();
      const next = value.hours >= 12 ? 1 : value.hours + 1;
      onChange({ ...value, hours: next });
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      clearPending();
      const next = value.hours <= 1 ? 12 : value.hours - 1;
      onChange({ ...value, hours: next });
    } else if (e.key === 'ArrowRight' || (e.key === 'Tab' && !e.shiftKey)) {
      if (e.key === 'ArrowRight') e.preventDefault();
      if (e.key === 'Tab') e.preventDefault();
      clearPending();
      minRef.current?.focus();
    } else if (/^\d$/.test(e.key)) {
      e.preventDefault();
      const digit = parseInt(e.key, 10);

      if (pendingDigitRef.current != null) {
        // Second digit → combine
        const combined = pendingDigitRef.current * 10 + digit;
        clearPending();
        onChange({ ...value, hours: clampHour(combined) });
        minRef.current?.focus();
      } else if (digit >= 2) {
        // Single digit ≥ 2 → can only be 2–9, commit immediately
        clearPending();
        onChange({ ...value, hours: clampHour(digit) });
        minRef.current?.focus();
      } else {
        // digit is 0 or 1 → wait for second digit
        pendingDigitRef.current = digit;
        // Show the pending digit immediately
        onChange({ ...value, hours: digit === 0 ? value.hours : digit });
        pendingTimerRef.current = setTimeout(() => {
          // Timeout — commit the single digit
          onChange({ ...value, hours: clampHour(digit === 0 ? 10 : digit) });
          pendingDigitRef.current = null;
          minRef.current?.focus();
        }, 800);
      }
    }
  };

  const handleMinuteKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      clearPending();
      onChange({ ...value, minutes: clampMinute(value.minutes + 1) });
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      clearPending();
      onChange({ ...value, minutes: clampMinute(value.minutes - 1) });
    } else if (e.key === 'ArrowLeft' || (e.key === 'Tab' && e.shiftKey)) {
      e.preventDefault();
      clearPending();
      hourRef.current?.focus();
    } else if (e.key === 'ArrowRight' || (e.key === 'Tab' && !e.shiftKey)) {
      if (e.key === 'ArrowRight') e.preventDefault();
      if (e.key === 'Tab') e.preventDefault();
      clearPending();
      periodRef.current?.focus();
    } else if (/^\d$/.test(e.key)) {
      e.preventDefault();
      const digit = parseInt(e.key, 10);

      if (pendingDigitRef.current != null) {
        const combined = pendingDigitRef.current * 10 + digit;
        clearPending();
        onChange({ ...value, minutes: clampMinute(combined) });
        periodRef.current?.focus();
      } else if (digit >= 6) {
        // Single digit ≥ 6 → can only be 06–09, commit immediately
        clearPending();
        onChange({ ...value, minutes: digit });
        periodRef.current?.focus();
      } else {
        pendingDigitRef.current = digit;
        onChange({ ...value, minutes: digit });
        pendingTimerRef.current = setTimeout(() => {
          pendingDigitRef.current = null;
          periodRef.current?.focus();
        }, 800);
      }
    }
  };

  const handlePeriodKey = (e: React.KeyboardEvent) => {
    if (
      e.key === 'ArrowUp' ||
      e.key === 'ArrowDown' ||
      e.key === 'a' ||
      e.key === 'A' ||
      e.key === 'p' ||
      e.key === 'P'
    ) {
      e.preventDefault();
      if (e.key === 'a' || e.key === 'A') {
        onChange({ ...value, period: 'AM' });
      } else if (e.key === 'p' || e.key === 'P') {
        onChange({ ...value, period: 'PM' });
      } else {
        onChange({ ...value, period: value.period === 'AM' ? 'PM' : 'AM' });
      }
    } else if (e.key === 'ArrowLeft' || (e.key === 'Tab' && e.shiftKey)) {
      e.preventDefault();
      minRef.current?.focus();
    }
  };

  const segmentBase =
    'outline-none cursor-default select-none rounded px-0.5 focus:bg-primary focus:text-primary-foreground';

  return (
    <div
      className={`bg-background text-foreground border-border hover:border-border-hover flex h-8 items-center gap-px rounded border px-2 font-mono text-xs tabular-nums ${ringClass ?? ''}`}
      onFocus={onFocus}
    >
      <span
        ref={hourRef}
        role="spinbutton"
        tabIndex={0}
        aria-label="Hours"
        aria-valuenow={value.hours}
        aria-valuemin={1}
        aria-valuemax={12}
        className={segmentBase}
        onKeyDown={handleHourKey}
      >
        {value.hours}
      </span>
      <span className="text-muted-foreground" aria-hidden>
        :
      </span>
      <span
        ref={minRef}
        role="spinbutton"
        tabIndex={0}
        aria-label="Minutes"
        aria-valuenow={value.minutes}
        aria-valuemin={0}
        aria-valuemax={59}
        className={segmentBase}
        onKeyDown={handleMinuteKey}
      >
        {value.minutes.toString().padStart(2, '0')}
      </span>
      <span
        ref={periodRef}
        role="spinbutton"
        tabIndex={0}
        aria-label="AM/PM"
        className={`${segmentBase} ml-1`}
        onKeyDown={handlePeriodKey}
      >
        {value.period}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mini calendar (self-contained)
// ---------------------------------------------------------------------------

const WEEK_DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function MiniCalendar({
  selected,
  rangeStart,
  rangeEnd,
  onSelect,
  minDate,
  maxDate,
  // selecting,
}: Readonly<{
  selected: Date | undefined;
  rangeStart?: Date;
  rangeEnd?: Date;
  onSelect: (d: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  selecting?: 'start' | 'end';
}>) {
  const [viewMonth, setViewMonth] = useState(() => selected ?? new Date());

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDow = new Date(year, month, 1).getDay();
  const monthLabel = viewMonth.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
  const today = new Date();

  // Last day of the previous month — if it's before minDate the whole prev month is inaccessible
  const prevDisabled =
    minDate != null &&
    startOfDay(new Date(year, month, 0)) < startOfDay(minDate);
  const nextDisabled =
    maxDate != null && new Date(year, month + 1, 1) > maxDate;

  // Build 6-row grid of day numbers (0 = empty)
  const cells: number[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(0);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(0);

  const orderedRange =
    rangeStart && rangeEnd
      ? startOfDay(rangeStart).getTime() <= startOfDay(rangeEnd).getTime()
        ? { start: startOfDay(rangeStart), end: startOfDay(rangeEnd) }
        : { start: startOfDay(rangeEnd), end: startOfDay(rangeStart) }
      : null;
  const isSingleDayRange = orderedRange
    ? orderedRange.start.getTime() === orderedRange.end.getTime()
    : false;

  const isSelected = (day: number) =>
    selected != null &&
    selected.getDate() === day &&
    selected.getMonth() === month &&
    selected.getFullYear() === year;

  const isToday = (day: number) =>
    today.getDate() === day &&
    today.getMonth() === month &&
    today.getFullYear() === year;

  const getCellDate = (day: number) => startOfDay(new Date(year, month, day));

  const isRangeStart = (day: number) =>
    orderedRange != null &&
    getCellDate(day).getTime() === orderedRange.start.getTime();

  const isRangeEnd = (day: number) =>
    orderedRange != null &&
    getCellDate(day).getTime() === orderedRange.end.getTime();

  const isInRange = (day: number) => {
    if (orderedRange == null) return false;
    const value = getCellDate(day).getTime();
    return (
      value >= orderedRange.start.getTime() &&
      value <= orderedRange.end.getTime()
    );
  };

  const isDisabled = (day: number) => {
    if (day === 0) return true;
    const cellDay = startOfDay(new Date(year, month, day));
    if (maxDate != null && cellDay > startOfDay(maxDate)) return true;
    if (minDate != null && cellDay < startOfDay(minDate)) return true;
    return false;
  };

  return (
    <div className="select-none">
      {/* Month header */}
      <div className="mb-1 flex items-center justify-between">
        <span className="text-foreground text-xs font-medium">
          {monthLabel}
        </span>
        <div className="flex gap-0.5">
          <Button
            variant="ghost"
            size="icon-xs"
            disabled={prevDisabled}
            onClick={() => setViewMonth(new Date(year, month - 1, 1))}
            aria-label="Previous month"
          >
            <ChevronLeft size={getIconPixels('sm', 'desktop')} />
          </Button>
          <Button
            variant="ghost"
            size="icon-xs"
            disabled={nextDisabled}
            onClick={() => setViewMonth(new Date(year, month + 1, 1))}
            aria-label="Next month"
          >
            <ChevronRight size={getIconPixels('sm', 'desktop')} />
          </Button>
        </div>
      </div>

      {/* Week-day headers */}
      <div className="grid grid-cols-7 text-center">
        {WEEK_DAYS.map((wd, i) => (
          <span
            key={i}
            className="text-muted-foreground pb-1 text-[10px] font-medium"
          >
            {wd}
          </span>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 text-center">
        {cells.map((day, i) => {
          if (day === 0) return <span key={i} />;
          const disabled = isDisabled(day);
          const sel = isSelected(day);
          const todayCell = isToday(day);
          const inRange = isInRange(day) && !disabled;
          const rangeStartDay = isRangeStart(day) && !disabled;
          const rangeEndDay = isRangeEnd(day) && !disabled;
          const rangeMiddle = inRange && !rangeStartDay && !rangeEndDay;
          const showTube = inRange && !isSingleDayRange;
          const tubeClass = rangeStartDay
            ? 'left-1/2 right-0'
            : rangeEndDay
              ? 'left-0 right-1/2'
              : 'left-0 right-0';
          const showSelectedCircle =
            rangeStartDay || rangeEndDay || (sel && !inRange);

          return (
            <div
              key={i}
              className="relative flex h-8 items-center justify-center"
            >
              {showTube && (
                <div
                  aria-hidden="true"
                  className={[
                    'bg-primary/18 absolute top-1/2 h-7 -translate-y-1/2',
                    tubeClass,
                  ].join(' ')}
                />
              )}
              <button
                type="button"
                disabled={disabled}
                onClick={() => {
                  const d = new Date(year, month, day);
                  if (selected) {
                    d.setHours(
                      selected.getHours(),
                      selected.getMinutes(),
                      0,
                      0
                    );
                  }
                  onSelect(d);
                }}
                className={[
                  'relative z-10 flex h-7 w-7 cursor-pointer items-center justify-center text-xs transition-colors',
                  disabled && 'text-muted-foreground/40 cursor-default',
                  showSelectedCircle &&
                    'bg-primary text-primary-foreground rounded-full',
                  rangeMiddle && 'text-foreground',
                  !showSelectedCircle &&
                    !rangeMiddle &&
                    !disabled &&
                    'hover:bg-muted rounded-full',
                  todayCell &&
                    !showSelectedCircle &&
                    !rangeMiddle &&
                    'text-primary font-semibold',
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-label={new Date(year, month, day).toLocaleDateString(
                  'en-US',
                  {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  }
                )}
                aria-pressed={sel}
              >
                {day}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Calendar popover content
// ---------------------------------------------------------------------------

function CalendarPanel({
  from,
  to,
  onApply,
}: Readonly<{
  from: Date | undefined;
  to: Date | undefined;
  onApply: (from: Date, to: Date) => void;
}>) {
  const [startDate, setStartDate] = useState<Date>(from ?? new Date());
  const [endDate, setEndDate] = useState<Date>(to ?? new Date());
  const [startDateStr, setStartDateStr] = useState(
    formatDateShort(from ?? new Date())
  );
  const [endDateStr, setEndDateStr] = useState(
    formatDateShort(to ?? new Date())
  );
  const [startTime, setStartTime] = useState<TimeParts>(
    dateToTimeParts(from ?? new Date())
  );
  const [endTime, setEndTime] = useState<TimeParts>(
    dateToTimeParts(to ?? new Date())
  );
  const [selecting, setSelecting] = useState<'start' | 'end'>('start');

  const applyTime = (date: Date, parts: TimeParts): Date => {
    const { h, m } = timePartsTo24(parts);
    const d = new Date(date);
    d.setHours(h, m, 0, 0);
    return d;
  };

  const handleApply = () => {
    const f = applyTime(startDate, startTime);
    const t = applyTime(endDate, endTime);
    onApply(f, t);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
    }
  };

  const handleCalendarSelect = (d: Date) => {
    if (selecting === 'start') {
      setStartDate(d);
      setStartDateStr(formatDateShort(d));
      setStartTime(dateToTimeParts(d));
      setSelecting('end');
    } else {
      setEndDate(d);
      setEndDateStr(formatDateShort(d));
      setEndTime(dateToTimeParts(d));
      setSelecting('start');
    }
  };

  const handleStartDateChange = (value: string) => {
    setStartDateStr(value);
    const parsed = parseDateInput(value);
    if (!parsed) return;
    parsed.setHours(startDate.getHours(), startDate.getMinutes(), 0, 0);
    setStartDate(parsed);
  };

  const handleEndDateChange = (value: string) => {
    setEndDateStr(value);
    const parsed = parseDateInput(value);
    if (!parsed) return;
    parsed.setHours(endDate.getHours(), endDate.getMinutes(), 0, 0);
    setEndDate(parsed);
  };

  const startRing = selecting === 'start' ? 'ring-primary ring-1' : '';
  const endRing = selecting === 'end' ? 'ring-primary ring-1' : '';

  return (
    <div className="flex flex-col gap-3" onKeyDown={handleKeyDown}>
      {/* Start row */}
      <div>
        <label
          className={`mb-1 block text-[11px] font-medium tracking-wide uppercase transition-colors ${
            selecting === 'start' ? 'text-primary' : 'text-muted-foreground'
          }`}
        >
          Start
        </label>
        <div className="flex gap-2">
          <Input
            type="text"
            value={startDateStr}
            onChange={(e) => handleStartDateChange(e.target.value)}
            onFocus={() => setSelecting('start')}
            className={`h-8 flex-1 text-xs ${startRing}`}
            placeholder="Apr 22, 2026"
          />
          <SegmentedTimeInput
            value={startTime}
            onChange={setStartTime}
            onFocus={() => setSelecting('start')}
            ringClass={startRing}
          />
        </div>
      </div>

      {/* End row */}
      <div>
        <label
          className={`mb-1 block text-[11px] font-medium tracking-wide uppercase transition-colors ${
            selecting === 'end' ? 'text-primary' : 'text-muted-foreground'
          }`}
        >
          End
        </label>
        <div className="flex gap-2">
          <Input
            type="text"
            value={endDateStr}
            onChange={(e) => handleEndDateChange(e.target.value)}
            onFocus={() => setSelecting('end')}
            className={`h-8 flex-1 text-xs ${endRing}`}
            placeholder="Apr 22, 2026"
          />
          <SegmentedTimeInput
            value={endTime}
            onChange={setEndTime}
            onFocus={() => setSelecting('end')}
            ringClass={endRing}
          />
        </div>
      </div>

      {/* Calendar */}
      <MiniCalendar
        selected={selecting === 'start' ? startDate : endDate}
        rangeStart={startDate}
        rangeEnd={endDate}
        onSelect={handleCalendarSelect}
        minDate={selecting === 'end' ? startOfDay(startDate) : undefined}
        maxDate={new Date()}
        selecting={selecting}
      />
      {/* Apply */}
      <Button
        variant="outline"
        size="sm"
        onClick={handleApply}
        className="w-full text-xs"
      >
        Apply
        <span className="ml-1 text-[10px] opacity-60">↵</span>
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preset dropdown
// ---------------------------------------------------------------------------

function PresetDropdown({
  presets,
  activeValue,
  activeLabel,
  onSelect,
}: Readonly<{
  presets: DateRangePreset[];
  activeValue: string;
  activeLabel?: string;
  onSelect: (key: string) => void;
}>) {
  const [open, setOpen] = useState(false);

  const triggerLabel =
    activeLabel ??
    presets.find((p) => p.value === activeValue)?.label ??
    activeValue;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger className="border-border text-muted-foreground hover:text-foreground hover:bg-background-hover flex h-full flex-1 cursor-pointer items-center justify-between gap-1 border-l px-1.5 transition-colors">
        <Text size="sm">{triggerLabel}</Text>
        <ChevronDown
          size={getIconPixels('xs', 'desktop')}
          className={`transition-transform duration-200 ease-out ${open ? 'rotate-180' : 'rotate-0'}`}
        />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto min-w-41.25 p-1">
        {presets.map((p) => (
          <button
            key={p.value}
            type="button"
            role="option"
            aria-selected={p.value === activeValue}
            onClick={() => {
              onSelect(p.value);
              setOpen(false);
            }}
            className={[
              'flex w-full cursor-pointer items-center rounded-sm px-3 py-1.5 text-left text-xs transition-colors',
              p.value === activeValue
                ? 'bg-muted text-foreground font-medium'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
            ].join(' ')}
          >
            {p.label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function DateRangePicker({
  value,
  onChange,
  presets,
  customPresetKey = 'custom',
  size = 'sm',
  className,
  statusSlot,
  resolvePreset,
}: Readonly<DateRangePickerProps>) {
  const [calendarOpen, setCalendarOpen] = useState(false);
  // Guard: when we programmatically open the calendar after the user picks
  // "Custom" from the preset dropdown, Radix fires a dismiss (the original
  // click registers as "pointer down outside" the calendar content). This ref
  // tells onOpenChange to ignore that spurious close.
  const suppressDismissRef = useRef(false);

  const sizeTokens = interactiveSizes[size];

  const handleCalendarOpenChange = (open: boolean) => {
    if (!open && suppressDismissRef.current) return;
    setCalendarOpen(open);
  };

  const handlePresetSelect = (key: string) => {
    if (key === customPresetKey) {
      // Only reset dates if not already on custom (re-clicking opens calendar to edit).
      if (value.preset !== customPresetKey) {
        onChange({ preset: customPresetKey, from: undefined, to: undefined });
      }
      suppressDismissRef.current = true;
      // Defer so the preset dropdown's Radix dismiss completes first.
      setTimeout(() => {
        setCalendarOpen(true);
        // Re-enable normal dismiss after Radix has settled.
        requestAnimationFrame(() => {
          suppressDismissRef.current = false;
        });
      }, 0);
      return;
    }
    if (resolvePreset) {
      const range = resolvePreset(key);
      onChange({ preset: key, from: range?.from, to: range?.to });
    } else {
      onChange({ preset: key, from: value.from, to: value.to });
    }
  };

  const handleCalendarApply = (from: Date, to: Date) => {
    onChange({ preset: customPresetKey, from, to });
    setCalendarOpen(false);
  };

  return (
    <div className={`relative flex w-full${className ? ` ${className}` : ''}`}>
      {/* Combined trigger bar */}
      <div
        className={`border-border bg-background text-foreground flex w-full items-stretch overflow-hidden rounded-md border ${sizeTokens.text}`}
      >
        {/* Calendar trigger (left side) */}
        <Popover open={calendarOpen} onOpenChange={handleCalendarOpenChange}>
          <PopoverTrigger
            className={`hover:bg-background-hover flex cursor-pointer items-center gap-1.5 transition-colors ${sizeTokens.padding}`}
          >
            <Calendar size={getIconPixels('xs', 'desktop')} />
          </PopoverTrigger>
          <PopoverContent align="start" className="w-82">
            <CalendarPanel
              from={value.from}
              to={value.to}
              onApply={handleCalendarApply}
            />
          </PopoverContent>
        </Popover>

        {/* Preset trigger (right chevron) */}
        <PresetDropdown
          presets={presets}
          activeValue={value.preset}
          activeLabel={
            value.preset === customPresetKey && value.from && value.to
              ? `${formatDateShort(value.from)} – ${formatDateShort(value.to)}`
              : undefined
          }
          onSelect={handlePresetSelect}
        />
        {statusSlot && (
          <div className="border-border flex shrink-0 items-center border-l px-2.5">
            {statusSlot}
          </div>
        )}
      </div>
    </div>
  );
}

DateRangePicker.displayName = 'DateRangePicker';

export default DateRangePicker;
